import React from 'react';
import { 
  Heart, 
  Users, 
  Stethoscope, 
  Brain, 
  Activity, 
  Zap, 
  Star, 
  Award,
  FileText,
  Code,
  Bug,
  Lightbulb,
  MessageCircle,
  Languages,
  AlertTriangle
} from 'lucide-react';
import { useExampleData } from '../data/example/ExampleDataProvider';
import { MockBadge } from '../components/common/MockBadge';
import { DataDisclaimer } from '../components/common/DataDisclaimer';
import { getThemeClass } from '../styles/theme';

interface FeatureInspiration {
  feature: string;
  inspiringPerson: string;
  relationship: string;
  background: string;
  challenge: string;
  solution: string;
  impact: string;
  image?: string;
}

interface Contributor {
  name: string;
  contribution: string;
  category: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface AboutProps {
  onBackToLanding?: () => void;
}

export const About: React.FC<AboutProps> = ({ onBackToLanding }) => {
  const { getContributors, getFeatureInspirations, getContributorCategories, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('about');

  // Load data from centralized example data
  const contributors = isExampleData ? getContributors() : [];
  const featureInspirations = isExampleData ? getFeatureInspirations() : [];
  const contributorCategories = isExampleData ? getContributorCategories() : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-6xl mx-auto px-6 py-12">
        
        {/* Header */}
        <div className="text-center mb-16">
          {onBackToLanding && (
            <div className="flex justify-start mb-6">
              <button 
                onClick={onBackToLanding}
                className="text-slate-600 hover:text-slate-800 font-medium"
              >
                ← Back to Home
              </button>
            </div>
          )}
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Heart className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              About ZippyHealth.io {isExampleData && <MockBadge variant="small" className="ml-2" />}
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Building the future of healthcare technology, one personal story at a time. 
            Every feature on our platform has a human story behind it.
          </p>
        </div>

        {/* Mission Statement */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Zap className="h-6 w-6 text-blue-600" />
            <h2 className={`text-2xl font-bold ${getThemeClass('text', 'primary')}`}>Our Mission</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            ZippyHealth.io exists to democratize healthcare data management and empower individuals 
            to take control of their health through intelligent technology. We believe that everyone 
            deserves access to the same level of health analytics and insights that were once only 
            available to large medical institutions.
          </p>
        </div>

        {/* Example Data Disclaimer */}
        {isExampleData && (
          <DataDisclaimer 
            type="info"
            title="Example About Data"
            message="This page contains example contributor data for demonstration. Real contributor data shows actual community members and their contributions."
            className="mb-8"
          />
        )}

        {/* Feature Inspirations */}
        <div className="mb-16">
          <h2 className={`text-3xl font-bold ${getThemeClass('text', 'primary')} mb-8 text-center`}>Features Inspired by Real People</h2>
          <div className="space-y-8">
            {featureInspirations.map((inspiration, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="text-6xl">{inspiration.image}</div>
                    <div className="flex-1">
                      <h3 className={`text-2xl font-bold ${getThemeClass('text', 'primary')} mb-2`}>{inspiration.feature}</h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold text-gray-700 flex items-center space-x-2">
                              <Users className="h-4 w-4" />
                              <span>Inspiration</span>
                            </h4>
                            <p className="text-gray-600">
                              <strong>{inspiration.inspiringPerson}</strong> - {inspiration.relationship}
                            </p>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-700 flex items-center space-x-2">
                              <Stethoscope className="h-4 w-4" />
                              <span>Background</span>
                            </h4>
                            <p className="text-gray-600">{inspiration.background}</p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold text-gray-700 flex items-center space-x-2">
                              <AlertTriangle className="h-4 w-4" />
                              <span>Challenge</span>
                            </h4>
                            <p className="text-gray-600">{inspiration.challenge}</p>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-700 flex items-center space-x-2">
                              <Brain className="h-4 w-4" />
                              <span>Our Solution</span>
                            </h4>
                            <p className="text-gray-600">{inspiration.solution}</p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-gray-700 flex items-center space-x-2 mb-2">
                          <Activity className="h-4 w-4" />
                          <span>Real Impact</span>
                        </h4>
                        <p className="text-gray-700">{inspiration.impact}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contributor Categories */}
        <div className="mb-16">
          <h2 className={`text-3xl font-bold ${getThemeClass('text', 'primary')} mb-8 text-center`}>How You Can Contribute</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {contributorCategories.map((category, index) => (
              <div key={index} className={`p-6 rounded-xl border ${category.color}`}>
                <div className="flex items-center space-x-3 mb-3">
                  {category.icon}
                  <h3 className="font-semibold">{category.category}</h3>
                </div>
                <p className="text-sm opacity-80">{category.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Every contribution, no matter how small, helps make healthcare technology more accessible and effective.
            </p>
            <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all">
              Start Contributing Today
            </button>
          </div>
        </div>

        {/* Contributors Hall of Fame */}
        <div className="mb-16">
          <h2 className={`text-3xl font-bold ${getThemeClass('text', 'primary')} mb-8 text-center`}>Contributors Hall of Fame</h2>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="space-y-6">
              {contributors.map((contributor, index) => (
                <div key={index} className={`p-6 rounded-xl border ${contributor.color}`}>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {contributor.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-bold">{contributor.name}</h3>
                        <span className={`px-3 py-1 text-xs rounded-full ${contributor.color}`}>
                          {contributor.category}
                        </span>
                      </div>
                      <p className="font-medium text-gray-700 mb-2">{contributor.contribution}</p>
                      <p className="text-sm text-gray-600">{contributor.description}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Placeholder for future contributors */}
              <div className="p-6 border-2 border-dashed border-gray-300 rounded-xl text-center">
                <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Your Name Could Be Here!</h3>
                <p className="text-gray-500">
                  Contribute to ZippyHealth.io and be recognized for making healthcare technology better for everyone.
                </p>
                <button className="mt-4 bg-gray-100 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                  Learn How to Contribute
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Team Information */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className={`text-2xl font-bold ${getThemeClass('text', 'primary')} mb-6 text-center`}>About the Team</h2>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">EH</span>
              </div>
              <h3 className={`text-xl font-bold ${getThemeClass('text', 'primary')} mb-2`}>Eric Henderson</h3>
              <p className="text-gray-600 mb-4">Founder & Lead Developer, Zippy Technologies LLC</p>
              <p className="text-gray-700 leading-relaxed">
                Eric founded ZippyHealth.io with the vision of making healthcare data management 
                accessible to everyone. Inspired by family members like Aunt Maggie who needed 
                better tools to manage their health conditions, Eric combines technical expertise 
                with real-world healthcare challenges to build solutions that actually work for patients.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Code className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className={`font-semibold ${getThemeClass('text', 'primary')} mb-2`}>Technical Innovation</h4>
                <p className="text-sm text-gray-600">Building cutting-edge healthcare technology</p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Heart className="h-6 w-6 text-green-600" />
                </div>
                <h4 className={`font-semibold ${getThemeClass('text', 'primary')} mb-2`}>Patient-Centered Design</h4>
                <p className="text-sm text-gray-600">Every feature solves real patient problems</p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <h4 className={`font-semibold ${getThemeClass('text', 'primary')} mb-2`}>Community Driven</h4>
                <p className="text-sm text-gray-600">Built with and for the healthcare community</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact & Connect */}
        <div className="text-center">
          <h2 className={`text-2xl font-bold ${getThemeClass('text', 'primary')} mb-4`}>Get Involved</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            ZippyHealth.io is more than just a platform - it's a community working together to improve healthcare technology. 
            Whether you're a patient, healthcare provider, developer, or just someone who believes in better healthcare tools, 
            there's a place for you here.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
              Send Feedback
            </button>
            <button className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors">
              View Documentation
            </button>
            <button className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors">
              Suggest a Feature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About; 
